/**
 * Validation utilities for Wild Train
 *
 * Provides validation functions for Workout and Exercise entities
 * following the rules defined in docs/data-model.md
 */

/**
 * Workout type enum
 */
export const WORKOUT_TYPES = {
  STRENGTH: 'strength',
  CARDIO: 'cardio',
  FLEXIBILITY: 'flexibility',
  SPORTS: 'sports',
  OTHER: 'other'
}

/**
 * Validation error class
 */
export class ValidationError extends Error {
  constructor(field, message) {
    super(message)
    this.name = 'ValidationError'
    this.field = field
  }
}

/**
 * Validate workout object
 *
 * @param {Object} workout - Workout object to validate
 * @returns {Object} { valid: boolean, errors: Array<ValidationError> }
 */
export function validateWorkout(workout) {
  const errors = []

  // Required: id (UUID v4 format)
  if (!workout.id) {
    errors.push(new ValidationError('id', 'ID is required'))
  } else if (!isValidUUID(workout.id)) {
    errors.push(new ValidationError('id', 'ID must be a valid UUID v4'))
  }

  // Required: date (valid ISO 8601 datetime)
  if (!workout.date) {
    errors.push(new ValidationError('date', '日期為必填'))
  } else if (!isValidDate(workout.date)) {
    errors.push(new ValidationError('date', '日期格式不正確'))
  }

  // Required: type (must be one of the allowed values)
  if (!workout.type) {
    errors.push(new ValidationError('type', '訓練類型為必填'))
  } else if (!Object.values(WORKOUT_TYPES).includes(workout.type)) {
    errors.push(new ValidationError('type', '訓練類型不正確'))
  }

  // Optional: duration (positive integer)
  if (workout.duration !== undefined && workout.duration !== null && workout.duration !== '') {
    const duration = Number(workout.duration)
    if (!Number.isInteger(duration) || duration <= 0) {
      errors.push(new ValidationError('duration', '訓練時長必須為正整數'))
    }
  }

  // Optional: notes (max 500 characters)
  if (workout.notes && workout.notes.length > 500) {
    errors.push(new ValidationError('notes', '備註最多 500 個字元'))
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate exercise object
 *
 * @param {Object} exercise - Exercise object to validate
 * @returns {Object} { valid: boolean, errors: Array<ValidationError> }
 */
export function validateExercise(exercise) {
  const errors = []

  // Required: id (UUID v4 format)
  if (!exercise.id) {
    errors.push(new ValidationError('id', 'ID is required'))
  } else if (!isValidUUID(exercise.id)) {
    errors.push(new ValidationError('id', 'ID must be a valid UUID v4'))
  }

  // Required: workout_id (must be valid UUID)
  if (!exercise.workout_id) {
    errors.push(new ValidationError('workout_id', 'Workout ID is required'))
  } else if (!isValidUUID(exercise.workout_id)) {
    errors.push(new ValidationError('workout_id', 'Workout ID must be a valid UUID v4'))
  }

  // Required: exercise_name (max 100 characters)
  if (!exercise.exercise_name || exercise.exercise_name.trim() === '') {
    errors.push(new ValidationError('exercise_name', '運動名稱為必填'))
  } else if (exercise.exercise_name.length > 100) {
    errors.push(new ValidationError('exercise_name', '運動名稱最多 100 個字元'))
  }

  // Required: sets (1-99)
  if (!exercise.sets) {
    errors.push(new ValidationError('sets', '組數為必填'))
  } else {
    const sets = Number(exercise.sets)
    if (!Number.isInteger(sets) || sets < 1 || sets > 99) {
      errors.push(new ValidationError('sets', '組數必須為 1-99 的整數'))
    }
  }

  // Required: reps (1-999)
  if (!exercise.reps) {
    errors.push(new ValidationError('reps', '次數為必填'))
  } else {
    const reps = Number(exercise.reps)
    if (!Number.isInteger(reps) || reps < 1 || reps > 999) {
      errors.push(new ValidationError('reps', '次數必須為 1-999 的整數'))
    }
  }

  // Optional: weight (0-9999)
  if (exercise.weight !== undefined && exercise.weight !== null && exercise.weight !== '') {
    const weight = Number(exercise.weight)
    if (isNaN(weight) || weight < 0 || weight > 9999) {
      errors.push(new ValidationError('weight', '重量必須為 0-9999 的數字'))
    }
  }

  // Optional: rest_time (0-3600)
  if (exercise.rest_time !== undefined && exercise.rest_time !== null && exercise.rest_time !== '') {
    const restTime = Number(exercise.rest_time)
    if (!Number.isInteger(restTime) || restTime < 0 || restTime > 3600) {
      errors.push(new ValidationError('rest_time', '休息時間必須為 0-3600 的整數（秒）'))
    }
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Check if string is valid UUID v4
 *
 * @param {string} uuid - UUID string to validate
 * @returns {boolean}
 */
function isValidUUID(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

/**
 * Check if date string is valid ISO 8601 datetime
 *
 * @param {string|Date} date - Date to validate
 * @returns {boolean}
 */
function isValidDate(date) {
  if (date instanceof Date) {
    return !isNaN(date.getTime())
  }

  const dateObj = new Date(date)
  return !isNaN(dateObj.getTime())
}

/**
 * Format validation errors for display
 *
 * @param {Array<ValidationError>} errors - Array of validation errors
 * @returns {string} Formatted error message
 */
export function formatValidationErrors(errors) {
  if (errors.length === 0) return ''

  return errors.map(error => error.message).join('\n')
}

/**
 * Get error message for a specific field
 *
 * @param {Array<ValidationError>} errors - Array of validation errors
 * @param {string} field - Field name to get error for
 * @returns {string|null} Error message or null if no error
 */
export function getFieldError(errors, field) {
  const error = errors.find(err => err.field === field)
  return error ? error.message : null
}

/**
 * Validate weight record data
 * @param {Object} record - Weight record to validate
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateWeightRecord(record) {
  const errors = {}

  // Validate weight
  if (!record.weight) {
    errors.weight = '體重為必填欄位'
  } else {
    const weight = parseFloat(record.weight)
    if (isNaN(weight)) {
      errors.weight = '體重必須為數字'
    } else if (weight <= 0) {
      errors.weight = '體重必須大於 0'
    } else if (weight < 20 || weight > 300) {
      errors.weight = '體重範圍應在 20-300 kg 之間'
    }
  }

  // Validate body fat (optional)
  if (record.bodyFat !== null && record.bodyFat !== undefined && record.bodyFat !== '') {
    const bodyFat = parseFloat(record.bodyFat)
    if (isNaN(bodyFat)) {
      errors.bodyFat = '體脂率必須為數字'
    } else if (bodyFat < 0 || bodyFat > 100) {
      errors.bodyFat = '體脂率範圍應在 0-100% 之間'
    }
  }

  // Validate date
  if (!record.date) {
    errors.date = '日期為必填欄位'
  } else {
    const date = new Date(record.date)
    if (isNaN(date.getTime())) {
      errors.date = '日期格式不正確'
    } else if (date > new Date()) {
      errors.date = '日期不可為未來時間'
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  }
}
